import Vue from 'vue'
import Vuex from 'vuex'
import { format, addDays } from 'date-fns'
import { IS_PRODUCTION } from './libs/constants'
import { sum } from './libs/utils'
import { getNormalizedPlanData } from './libs/plan-helper'
import { getUserInfo } from '@/api/user'
import { getCurrentNext } from '@/api/userPlan'
import { getList as getABList } from '@/api/abtest'

Vue.use(Vuex)

const now = new Date()

const store = new Vuex.Store({
  state: {
    errorData: null,
    layout: {
      gnbMinimize: false,
      lnbMinimize: false
    },
    pageAddGuideFocusContent: {
      currentStep: 1,
      currentContentIndex: 0
    },
    currentDashboardCategory: {},
    isFirstLogin: false,
    stickyData: null,
    stickyDataId: null,
    showStickyData: false,
    showNoPvDialog: true,
    userData: null,
    planData: {},
    isDashboardEditMode: false,
    pageList: [],
    applyData: null,
    funnelData: null,
    ctaData: {},
    reportPeriod: {
      start: format(addDays(now, -6), 'YYYY-MM-DD'),
      end: format(now, 'YYYY-MM-DD')
    },
    abList: null,
    abPageName: null,
    showUserMenu: false,
    showReportPageList: false,
    showStreamTip: true,
    resizeTimestamp: 0,
    captureImageTimestamp: new Date().getTime(),
    myInfoTimestamp: null,
    dreamVersion: null
  },
  getters: {
    creditData (state) {
      const result = {
        hasPlan: false,
        isUnlimited: false,
        remain: 0,
        percentage: 0
      }

      const userData = state.userData
      const planData = state.planData

      if (userData && planData) {
        result.hasPlan = planData.hasPlan

        if (!userData.creditEmpty && planData.hasPlan) {
          result.remain = userData.remainPv

          if (result.remain === -1) {
            result.isUnlimited = true
            result.percentage = 100
          } else {
            result.percentage = userData.battery || 0
          }
        }
      }
      return result
    },
    abUsedTraffic (state) {
      if (!state.abList) {
        return 0
      }
      return Math.min(sum(state.abList, 'traffic'), 100)
    }
  },
  mutations: {
    setUserData (state, value) {
      state.userData = value
    },
    setPlanData (state, value) {
      state.planData = value
    },
    setErrorData (state, payload) {
      state.errorData = payload
    },
    setLnbMinimize (state) {
      state.layout.lnbMinimize = !state.layout.lnbMinimize
    },
    setGnbMinimize (state) {
      state.layout.gnbMinimize = !state.layout.gnbMinimize
    },
    setCurrentDashboardCategory (state, payload) {
      state.currentDashboardCategory = payload
    },
    setIsFirstLogin (state, payload) {
      state.isFirstLogin = payload
    },
    setShowUserMenu (state, value) {
      state.showUserMenu = value
    },
    setIsDashboardEditMode (state, payload) {
      state.isDashboardEditMode = payload
    },
    setShowReportPageList (state, value) {
      state.showReportPageList = value
    },
    setReportPeriod (state, value) {
      state.reportPeriod = value
    },
    setApplyData (state, value) {
      state.applyData = value
    },
    setFunnelData (state, value) {
      state.funnelData = value
    },
    setCtaData (state, payload) {
      state.ctaData = payload
    },
    setPageList (state, value) {
      state.pageList = value
    },
    setResizeTimestamp (state, value) {
      state.resizeTimestamp = value
    },
    setCaptureImageTimestamp (state, value) {
      state.captureImageTimestamp = value
    },
    setPageAddGuideContent (state, values) {
      state.pageAddGuideFocusContent = values
    },
    setABList (state, payload) {
      state.abList = payload
    },
    setABPageName (state, payload) {
      state.abPageName = payload
    },
    setStickyData (state, payload) {
      state.stickyData = payload
    },
    setStickyDataId (state, payload) {
      state.stickyDataId = payload
    },
    setShowStickyData (state, payload) {
      state.showStickyData = payload
    },
    setShowNoPvDialog (state, payload) {
      state.showNoPvDialog = payload
    },
    setShowStreamTip (state, payload) {
      state.showStreamTip = payload
    },
    setMyInfoTimestamp (state, payload) {
      state.myInfoTimestamp = payload
    },
    setDreamVersion (state, payload) {
      state.dreamVersion = payload
    },
    clearAllUserData (state) {
      state.userData = null
      state.planData = null
      state.stickyData = null
      state.stickyDataId = null
      state.showStickyData = true
      state.showNoPvDialog = true
      state.myInfoTimestamp = null
    }
  },
  actions: {
    storeUserData ({ dispatch, commit }) {
      return getUserInfo().then(data => {
        commit('setUserData', data)
        return data
      })
    },
    storePlanData ({ commit, state }) {
      const handlePlanData = (data) => {
        const planData = getNormalizedPlanData(data)
        let stickyDataId = null

        if (planData && planData.stickyData) {
          stickyDataId = planData.stickyData.id
          const oldStickyDataId = state.stickyDataId

          if (stickyDataId !== oldStickyDataId) {
            commit('setShowStickyData', true)
          }
        }
        commit('setStickyDataId', stickyDataId)
        commit('setPlanData', planData)
      }

      return getCurrentNext().then(data => {
        handlePlanData(data)
        return data
      }).catch(error => {
        if (error.message === 'No Plan found') {
          handlePlanData({})
        } else {
          throw error
        }
      })
    },
    storeABList ({ commit }, aid) {
      return getABList(aid).then(data => {
        data = data.filter(item => item.status !== 'deleted')
        commit('setABList', data)
        return data
      })
    }
  },
  strict: IS_PRODUCTION
})

export default store

export function getStore () {
  return store
}
